<template>
  <div class="container py-5">

    <div class="columns">
      <div class="buttons column is-9">
        <router-link
          :to="{ name: 'ProductionSheetMaker' }"
          class="button is-primary">
          Add a Production Sheet
        </router-link>
        <a class="button"
          v-if="selected.length > 0"
          @click="launchConfirmDelete(true)">
          <span class="icon is-medium">
            <i class="fas fa-trash has-text-danger"></i>
          </span>
          <span>Delete {{ selected.length }} Selected Production Sheet</span>
        </a>
      </div>
      <div class="column is-3">
        <search-autocomplete
          class="level-right" 
          :value="filters.project_id" 
          @input="setFilter({
            field: 'project_id', 
            value: $event
          })"/>
      </div>
    </div>

    <article 
      v-if="!firstLoad && productionSheets.length == 0" 
      class="message is-info is-fullwidth">
      <div class="message-body">
        There are no production sheets.
      </div>
    </article>

    <table 
      class="table is-fullwidth is-hoverable is-clickable">

      <thead>
        <tr>
          <th>
            <v-checkbox 
              :value="allSelected" 
              @input="toggleAllSelected($event)"/>
          </th>
          <th 
            v-for="(header, headerIndex) in headers"
            :key="headerIndex">
            {{ header }}
          </th>
          <th></th>
        </tr>
        <tr v-if="loading">
          <th :colspan="headers.length + 2" class="py-0 px-0 is-naked">
            <v-progress class="is-fullwidth" v-model="loading"/>
          </th>
        </tr>
      </thead>

      <v-fade-transition mode="out-in">
        <tbody
          :key="pagination.page" 
          v-if="!firstLoad && productionSheets.length > 0">
          <tr
            class="v-centered"
            v-for="productionSheet in productionSheets"
            :key="productionSheet.id"
            @click="view(productionSheet)">

            <td>
              <v-checkbox 
                @click.native.stop=""
                :value="selected.indexOf(productionSheet.id) != -1"
                @input="toggleSelected(productionSheet.id)"/>
            </td>
            <td>
              {{ productionSheet.project.project_name }}
            </td>
            <td>
              {{ productionSheet.name }}
            </td>
            <td>
              {{ productionSheet.desc }}
            </td>
            <td>
              <a 
                v-if="productionSheet.image"
                @click.stop=""
                :href="productionSheet.image" 
                target="_blank">
                attachment
              </a>
            </td>
            <td>
              {{ productionSheet.items.length }}
            </td>
            <td>
              {{ format(parse(productionSheet.created_on)) }}
            </td>
            <td>
              {{ productionSheet.created_user.display_name }}
            </td>

            <td>
              <div class="buttons buttons-right">
                <button 
                  @click.stop="deleteId = productionSheet.id; launchConfirmDelete()"
                  class="button is-small has-text-grey">
                  <span class="icon">
                    <i class="fas fa-trash"></i>
                  </span>
                  <span>Delete</span>
                </button>
              </div>
            </td>

          </tr>
        </tbody>
      </v-fade-transition>
      

    </table>

    <v-pagination 
      v-if="pagination.pages > 1"
      :value="pagination.page"
      @input="changePage"
      :length="pagination.pages"/>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmed()"
      @cancel="deleteId = null; confirmDelete.state = false"/>

  </div>
</template>

<script>
import VCheckbox from '@/components/base/VCheckbox'
import VConfirmModal from '@/components/base/VConfirmModal'
import VPagination from '@/components/base/VPagination'
import VProgress from '@/components/base/VProgress'
import VFadeTransition from '@/components/transitions/VFadeTransition'
import SearchAutocomplete from '@/components/projects/SearchAutocomplete'

import { mapState, mapActions, mapMutations } from 'vuex'

import { parseISO, format } from 'date-fns'

export default {
  components: { 
    VCheckbox,
    VConfirmModal, 
    VPagination,
    VProgress,
    VFadeTransition,
    SearchAutocomplete
  },
  data() {
    return {
      headers: [
        'Project', 'Name', 'Description', 'Attachment', 
        'Total Items', 'Created On', 'Created By'
      ],
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this production sheet?',
        type: 'danger'
      },
      deleteId: null,
      bulk: false,
      parse: (d) => {
        return d ? parseISO(d) : null
      },
      format: (d) => {
        return d ? format(d, 'yyyy-MM-dd H:mm:ss') : null
      }
    }
  },
  created() {
    this.initialize();
  },
  computed: {
    ...mapState("productionSheets", [
      "productionSheets", "pagination", "selected", 
      "firstLoad", "loading", "allSelected", "filters"
    ])
  },
  watch: {
    'pagination.page'() {
      this.getProductionSheets(this.pagination);
    },
    filters: {
      handler(filters) {
        this.getProductionSheets(filters);
      },
      deep: true
    }
  },
  methods: {
    ...mapActions("productionSheets", [
      "getProductionSheets", "deleteProductionSheet", "toggleAllSelected"
    ]),
    ...mapMutations("productionSheets", [
      "changePage", "toggleSelected", "setFilter"
    ]),
    initialize() {
      this.getProductionSheets();
    },
    launchConfirmDelete(bulk=false) {
      this.bulk = bulk;
      if (bulk) {
        this.confirmDelete.message = `Are you sure you want to delete ${this.selected.length} production sheets?`;
      } else {
        this.confirmDelete.message = 'Are you sure you want to delete this production sheet?'
      }
      this.confirmDelete.state = true;
    },
    deleteConfirmed() {
      this.confirmDelete.state = false;

      if (this.bulk == true) {
        this.deleteSelected();
      } else {
        this.deleteProductionSheet(this.deleteId).then(
          () => this.deleteId = null
        );
      }
    },
    deleteSelected() {
      this.selected.forEach(productionSheetId => {
        this.deleteProductionSheet(productionSheetId);
      })
    }, 
    view(productionSheet) {
      this.$router.push({
        name: 'ProductionSheetViewer',
        params: {
          productionSheetId: productionSheet.id,
          productionSheet: productionSheet
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
</style>